import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Copy, Plus, Loader2, CheckCircle, FileText, GitBranch
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CreateVersionModal = ({
  simulatorId,
  existingVersions = [],
  token,
  onSuccess,
  onCancel,
  lang = 'fr'
}) => {
  const [formData, setFormData] = useState({
    version_number: '',
    name: '',
    description: '',
    clone_from_version_id: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const t = {
    fr: {
      title: "Créer une nouvelle version",
      versionNumber: "Numéro de version",
      versionNumberPlaceholder: "Ex: 1.1.0, 2.0.0",
      name: "Nom de la version",
      namePlaceholder: "Ex: Mise à jour règles GCC",
      description: "Description",
      descriptionPlaceholder: "Décrivez les changements de cette version...",
      cloneFrom: "Cloner depuis une version existante",
      cloneNone: "Nouvelle version vide",
      cloneDescription: "Copie toutes les règles et paramètres de la version sélectionnée",
      create: "Créer la version",
      cancel: "Annuler",
      creating: "Création...",
      published: "publiée",
      rules: "règles"
    },
    en: {
      title: "Create new version",
      versionNumber: "Version number",
      versionNumberPlaceholder: "Ex: 1.1.0, 2.0.0",
      name: "Version name",
      namePlaceholder: "Ex: GCC rules update",
      description: "Description",
      descriptionPlaceholder: "Describe the changes in this version...",
      cloneFrom: "Clone from existing version",
      cloneNone: "New empty version",
      cloneDescription: "Copies all rules and parameters from the selected version",
      create: "Create version",
      cancel: "Cancel",
      creating: "Creating...",
      published: "published",
      rules: "rules"
    }
  };

  const suggestNextVersion = () => {
    if (existingVersions.length === 0) {
      return '1.0.0';
    }
    
    // Get latest version
    const latest = existingVersions[0]?.version_number || '1.0.0';
    const parts = latest.split('.').map(Number);
    
    // Increment patch version
    parts[2] = (parts[2] || 0) + 1;
    return parts.join('.');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.version_number || !formData.name) {
      setError('Numéro de version et nom requis');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const res = await fetch(`${API_URL}/api/admin/simulators/${simulatorId}/versions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (res.ok) {
        const data = await res.json();
        onSuccess && onSuccess(data.version);
      } else {
        const err = await res.json();
        setError(err.detail || 'Erreur lors de la création');
      }
    } catch {
      setError('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5" />
            {t[lang].title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error}
              </div>
            )}

            {/* Version number */}
            <div>
              <Label>{t[lang].versionNumber} *</Label>
              <div className="flex gap-2 mt-1">
                <Input
                  value={formData.version_number}
                  onChange={(e) => setFormData({ ...formData, version_number: e.target.value })}
                  placeholder={t[lang].versionNumberPlaceholder}
                  className="font-mono"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setFormData({ ...formData, version_number: suggestNextVersion() })}
                >
                  Auto
                </Button>
              </div>
            </div>

            {/* Name */}
            <div>
              <Label>{t[lang].name} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t[lang].namePlaceholder}
                className="mt-1"
              />
            </div>

            {/* Description */}
            <div>
              <Label>{t[lang].description}</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder={t[lang].descriptionPlaceholder}
                className="mt-1"
                rows={2}
              />
            </div>

            {/* Clone from */}
            <div>
              <Label className="flex items-center gap-2">
                <GitBranch className="w-4 h-4" />
                {t[lang].cloneFrom}
              </Label>
              <p className="text-xs text-gray-500 mb-2">{t[lang].cloneDescription}</p>
              
              <div className="space-y-2 max-h-48 overflow-y-auto border rounded-lg p-2">
                <label className="flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer">
                  <input
                    type="radio"
                    name="clone"
                    value=""
                    checked={!formData.clone_from_version_id}
                    onChange={() => setFormData({ ...formData, clone_from_version_id: '' })}
                  />
                  <FileText className="w-4 h-4 text-gray-400" />
                  <span className="text-sm">{t[lang].cloneNone}</span>
                </label>

                {existingVersions.map(v => (
                  <label
                    key={v.id}
                    className={`flex items-center gap-3 p-2 rounded cursor-pointer ${
                      formData.clone_from_version_id === v.id
                        ? 'bg-blue-50 border border-blue-200'
                        : 'hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="radio"
                      name="clone"
                      value={v.id}
                      checked={formData.clone_from_version_id === v.id}
                      onChange={() => setFormData({ ...formData, clone_from_version_id: v.id })}
                    />
                    <Copy className="w-4 h-4 text-blue-500" />
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm font-medium">{v.version_number}</span>
                        {v.status === 'published' && (
                          <Badge className="bg-green-700 text-white text-xs font-semibold">
                            {t[lang].published}
                          </Badge>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {v.name} • {v.rules_count || 0} {t[lang].rules}
                      </span>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={loading}
                className="flex-1"
              >
                {t[lang].cancel}
              </Button>
              <Button
                type="submit"
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    {t[lang].creating}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    {t[lang].create}
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default CreateVersionModal;
